import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { forgetProperty } from '../auth/lastProperty';

/**
 * B1-03 — the Attention list for one property. The product's operating centre
 * and, per F02, where switching property always lands.
 *
 * Three things this screen does that a conventional dashboard would not.
 *
 * **Unknown is shown, in the middle.** The API sorts it there and this renders
 * it as its own band rather than folding it into "OK" or hiding it behind a
 * filter. A property whose obligations are all Unknown should look alarming,
 * because it is — and a screen that omits what it cannot assess is spotless
 * for the worst-managed building in the portfolio.
 *
 * **Every count carries its basis.** No bare percentage anywhere. C9's founder
 * ruling keeps the domain language and requires the figure never to render
 * without its denominator; the sentence the API returns is shown verbatim
 * because it is what a board report will quote.
 *
 * **Every item shows where its requirement comes from.** A citation, a method
 * version, or the word Unknown. A requirement displayed with no source is one
 * the reader cannot check, which is how an invented rule survives review.
 *
 * **AND EVERY ITEM OPENS.** E07-S07: an Attention item opens directly into the
 * continuous workspace. Until Sprint 11 the titles here were `<span>`s — the
 * workspace existed at `/p/:id/issues/:type/:id`, was fully built, and nothing
 * in the product linked to it. S10B §3's first delivery principle is
 * "Preserve Attention → Why → Evidence → Related Intelligence → Action →
 * Outcome/History", and that chain was severed at its first arrow: the
 * operating centre was a list you could read and not enter.
 */

type Basis = {
  kind: string;
  citation: string | null;
  method_version: string | null;
};

type Item = {
  contributor: string;
  subject_id: string;
  title: string;
  band: 'current' | 'monitor' | 'attention' | 'unknown';
  severity: string;
  summary: string | null;
  due_date: string | null;
  needs_attention_by: string | null;
  overdue: boolean;
  days_until_attention: number | null;
  basis: Basis;
  /* E07-S04. Optional: an older API sends none of them, and "where
     available" is the criterion — an absent owner is shown as Unassigned,
     never guessed. */
  owner?: string | null;
  evidence?: string | null;
  next_action?: { id: string | null; title: string } | null;
};

type AttentionView = {
  property_id: string;
  as_of: string;
  counts: { by_band: Record<string, number>; total: number; basis: string };
  items: Item[];
};

/** C13's vocabulary, not Good / Fair / Poor. Unknown is a band. */
const BAND_LABEL: Record<Item['band'], string> = {
  current: 'Current',
  monitor: 'Monitor',
  attention: 'Attention',
  unknown: 'Unknown',
};

function BasisLine({ basis }: { basis: Basis }) {
  if (basis.kind === 'jurisdiction_rule' && basis.citation) {
    return <span className="basis">Requirement: {basis.citation}</span>;
  }
  if (basis.kind === 'assessment' && basis.method_version) {
    return <span className="basis">Assessed by method {basis.method_version}</span>;
  }
  if (basis.kind === 'user_stated') {
    return <span className="basis">Recorded by a person, not a regulation</span>;
  }
  // Said plainly rather than left blank. A blank reads as "no issue"; this
  // reads as "we cannot tell you where this comes from", which is the truth.
  return <span className="basis basis--unknown">Source: Unknown</span>;
}

function When({ item }: { item: Item }) {
  if (item.overdue && item.due_date) {
    return <span className="when when--overdue">Overdue since {item.due_date}</span>;
  }
  if (item.needs_attention_by) {
    const days = item.days_until_attention;
    return (
      <span className="when">
        Needs attention by {item.needs_attention_by}
        {days !== null && days >= 0 ? ` — ${days} day${days === 1 ? '' : 's'}` : ''}
      </span>
    );
  }
  // Not "today", not "soon". The contributor has not said, and inventing
  // urgency here is the same error as hiding it.
  return <span className="when when--unknown">No date recorded</span>;
}

/**
 * The title, as the way in.
 *
 * WHERE IT GOES DEPENDS ON WHAT IT IS, and the honest answer is not the same
 * for all three contributors. The issue workspace resolves `obligation` and
 * `asset`; an `action` is already its own screen with its own history, and
 * routing it through a workspace that cannot describe it would be a link to a
 * 404 dressed as a feature.
 *
 * A contributor this build cannot open stays plain text rather than becoming a
 * broken link. That case should not arise — the three contributors are all
 * handled — but a contributor added later must fail visibly here rather than
 * shipping a dead link on the product's busiest screen.
 */
function ItemTitle({ propertyId, item }: { propertyId: string; item: Item }) {
  const to =
    item.contributor === 'action'
      ? `/p/${propertyId}/actions/${item.subject_id}`
      : item.contributor === 'obligation' || item.contributor === 'asset'
        ? `/p/${propertyId}/issues/${item.contributor}/${item.subject_id}`
        : item.contributor === 'document'
          ? `/p/${propertyId}/evidence/${item.subject_id}`
          : item.contributor === 'document_request'
            ? `/p/${propertyId}/evidence/requests`
            : item.contributor === 'inspection'
              ? `/inspections/${item.subject_id}/result`
              : item.contributor === 'capital'
                ? `/p/${propertyId}/capital`
                : null;

  if (to === null) return <span className="title">{item.title}</span>;
  return (
    <Link className="title" to={to}>
      {item.title}
    </Link>
  );
}

/**
 * The dominant issue. E07-S03: "one dominant material issue plus supporting
 * items", and S8B §8's "first card visibly dominant".
 *
 * Attention rendered every item at equal weight, so the screen answered "here
 * are some things" when the question a strata manager opens it with is "what
 * should I do today".
 *
 * **MATERIAL IS THE LOAD-BEARING WORD.** The hero is the first item the product
 * has actually assessed as needing something — Attention or Monitor. It is not
 * simply the top of the list, because the top of the list is sometimes an item
 * nobody has assessed at all, and promoting that to hero would dress a gap in
 * our knowledge as a finding about the building.
 *
 * **When nothing is material there is no hero**, and the screen says which of
 * the two reasons applies. A hero invented from a quiet property is how a
 * screen starts crying wolf; "nothing needs attention" and "nothing has been
 * looked at" are different facts and DR-06 requires the second to survive.
 *
 * **Nothing is hidden by promoting it.** The hero is drawn from the same list
 * and the remainder follows it, so every item is still on screen and the counts
 * above are untouched — E07's acceptance criterion is that Unknown, Missing,
 * Stale and Conflicting cannot be hidden by aggregation.
 *
 * The tone is the item's own. The approved prototype hard-codes the poor red
 * here because its example hero is critical; a Monitor item drawn in red would
 * be a louder claim than the band it carries.
 */
function Hero({ item, propertyId }: { item: Item; propertyId: string }) {
  return (
    <div className={`hero band--${item.band}`}>
      <div className="hero__top">
        <span className={`band band--${item.band}`}>{BAND_LABEL[item.band]}</span>
        <When item={item} />
      </div>

      <h2 className="hero__title">
        <ItemTitle propertyId={propertyId} item={item} />
      </h2>

      {item.summary && item.summary !== item.title ? (
        <p className="hero__summary">{item.summary}</p>
      ) : null}

      {/* Both dates, separately, and neither derived from the other — RE-05.
          `When` above says which one is pressing; this says what they are. */}
      <div className="hero__facts">
        <span className="hero__fact">
          Needs attention by{' '}
          <strong>{item.needs_attention_by ?? <span className="unknown">Unknown</span>}</strong>
        </span>
        <span className="hero__fact">
          Due <strong>{item.due_date ?? <span className="unknown">Unknown</span>}</strong>
        </span>
      </div>

      <Accountability item={item} propertyId={propertyId} />

      <div className="hero__basis">
        <BasisLine basis={item.basis} />
      </div>
    </div>
  );
}

/**
 * What the screen says when nothing is material.
 *
 * Two different silences, and the difference is the whole product. A property
 * with everything assessed and current is fine. A property where nothing has
 * been assessed is not fine, and it must not read as though it were.
 */
/**
 * E07-S04 — who has it, what shows it, and what is being done.
 *
 * "Unassigned" is a state and is shown as one (WF-01); a blank would read as
 * "somebody, presumably". Where there is no next action the line says so, so
 * a material item nobody is working on is visibly that.
 */
function Accountability({ item, propertyId }: { item: Item; propertyId: string }) {
  if (item.owner === undefined && item.evidence === undefined && item.next_action === undefined)
    return null;
  return (
    <div className="accountability">
      <span>
        Owner <strong>{item.owner ?? <span className="unknown">Unassigned</span>}</strong>
      </span>
      {item.evidence && <span>{item.evidence}</span>}
      {item.contributor !== 'action' && (
        <span>
          Next action{' '}
          {item.next_action?.id ? (
            <Link to={`/p/${propertyId}/actions/${item.next_action.id}`}>
              {item.next_action.title}
            </Link>
          ) : (
            <span className="unknown">None yet</span>
          )}
        </span>
      )}
    </div>
  );
}

const FILTERS: [string, string][] = [
  ['all', 'Everything'],
  ['obligation', 'Obligations'],
  ['asset', 'Assets'],
  ['action', 'Actions'],
  ['inspection', 'Inspections'],
  ['capital', 'Capital'],
  ['evidence', 'Evidence'],
];

function matches(item: Item, filter: string): boolean {
  if (filter === 'all') return true;
  if (filter === 'evidence')
    return item.contributor === 'document' || item.contributor === 'document_request';
  return item.contributor === filter;
}

function NoHero({ items }: { items: Item[] }) {
  const unknown = items.filter((i) => i.band === 'unknown').length;
  if (unknown > 0) {
    return (
      <p className="hero hero--none band--unknown">
        Nothing here has been assessed as needing attention, but{' '}
        <strong>
          {unknown} of {items.length} item(s)
        </strong>{' '}
        have not been assessed at all. That is a gap in what has been recorded, not a finding about
        the building.
      </p>
    );
  }
  return (
    <p className="hero hero--none band--current">
      Nothing currently needs attention on this property. Everything below has been assessed and is
      within its timing.
    </p>
  );
}

export function Attention() {
  const { propertyId } = useParams();
  const [view, setView] = useState<AttentionView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    api
      .attention<AttentionView>(propertyId ?? '')
      .then((data) => !cancelled && setView(data))
      .catch((e) => {
        if (cancelled) return;
        /* A remembered property that cannot be loaded forgets itself.
           The stored organization id taught this: it was persisted forever,
           including after a test cleanup deleted the tenant behind it, and
           every later request carried an id the API refused — a 403 on every
           screen with no way out but a browser console. Sign-in lands here, so
           a stale memory would put somebody in exactly that loop. */
        forgetProperty();
        setError(String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  if (error) {
    return (
      <section>
        <h1>Attention</h1>
        <p role="alert">{error}</p>
      </section>
    );
  }

  if (!view) {
    return (
      <section>
        <h1>Attention</h1>
        <p aria-live="polite">Loading…</p>
      </section>
    );
  }

  /* The first item the product has assessed as needing something. The list is
     already ordered — overdue first, then by how soon, then by severity — so
     this takes the ordering rather than inventing a second one. */
  const hero = view.items.find((i) => i.band === 'attention' || i.band === 'monitor') ?? null;
  const rest = view.items.filter((i) => i !== hero);

  return (
    <section>
      <h1>Attention</h1>

      {/* A zero is not news. It keeps its word and its place — the reader
          still learns there are none — and stops competing with the counts
          that have something in them. */}
      <p className="counts counts--rings">
        {(['attention', 'monitor', 'unknown', 'current'] as const).map((band) => (
          <span
            key={band}
            className={`count count--${band}${(view.counts.by_band[band] ?? 0) === 0 ? ' count--zero' : ''}`}
          >
            <strong>{view.counts.by_band[band] ?? 0}</strong> {BAND_LABEL[band]}
          </span>
        ))}
      </p>
      {/* Verbatim from the API. This is the sentence a board report quotes. */}
      <p className="basis basis--summary">{view.counts.basis}</p>

      {view.items.length === 0 ? (
        <p>Nothing recorded for this property yet.</p>
      ) : (
        <>
          {hero ? (
            <Hero item={hero} propertyId={propertyId ?? ''} />
          ) : (
            <NoHero items={view.items} />
          )}

          {/* Named, so the reader knows the hero is not the whole story and
              that this is the rest of the same list rather than a second one. */}
          {rest.length > 0 && (
            <h2 className="hero__rest">
              {hero ? 'Also on this property' : 'Everything on this property'} — {rest.length}{' '}
              item(s)
            </h2>
          )}

          {/* S8B §8: simple filters. They narrow the list below and nothing
            else — the counts above stay whole, and the line says how much is
            being hidden, so a filter can never read as "nothing else". */}
          {rest.length > 0 && (
            <div className="filters" role="group" aria-label="Show">
              {FILTERS.filter(
                ([key]) => key === 'all' || key === filter || rest.some((i) => matches(i, key)),
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={filter === key}
                  onClick={() => setFilter(key)}
                >
                  {label} ({rest.filter((i) => matches(i, key)).length})
                </button>
              ))}
            </div>
          )}
          {filter !== 'all' && (
            <p className="basis">
              Showing {rest.filter((i) => matches(i, filter)).length} of {rest.length}. The counts
              above include everything.
            </p>
          )}
          <ul className="attention-list">
            {rest
              .filter((i) => matches(i, filter))
              .map((item) => (
                <li key={`${item.contributor}:${item.subject_id}`} className={`band--${item.band}`}>
                  {/* The band is a word, never colour alone — SO-11 and the
                  non-colour-only-state rule in E16-S14. */}
                  <span className={`band band--${item.band}`}>{BAND_LABEL[item.band]}</span>
                  <ItemTitle propertyId={propertyId ?? ''} item={item} />
                  {item.summary && item.summary !== item.title ? (
                    <span className="summary">{item.summary}</span>
                  ) : null}
                  <When item={item} />
                  <BasisLine basis={item.basis} />
                  <Accountability item={item} propertyId={propertyId ?? ''} />
                </li>
              ))}
          </ul>
        </>
      )}
    </section>
  );
}
